import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

const EMPTY_RIGHT = {
  name: '',
  right_type: 'Social Media',
  quantity: 1,
  notes: ''
}

export default function AllocationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { orgId } = useUser()

  const [allocation, setAllocation] = useState(null)
  const [rights, setRights] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [newRight, setNewRight] = useState(EMPTY_RIGHT)
  const [openRights, setOpenRights] = useState({})
  const [bulkEdits, setBulkEdits] = useState({})

  useEffect(() => {
    fetchAllocation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function toggleRightOpen(rightId) {
    setOpenRights(prev => ({ ...prev, [rightId]: !prev[rightId] }))
  }

  function deliveriesForRight(rightId) {
    return deliveries.filter(d => d.right_id === rightId)
  }

  function isDeliveryComplete(delivery) {
    return delivery.status === 'delivered' || delivery.status === 'approved'
  }

  function isRightComplete(right) {
    const rightDeliveries = deliveriesForRight(right.id)
    return rightDeliveries.length > 0 && rightDeliveries.every(isDeliveryComplete)
  }

  function rightProgress(right) {
    const rightDeliveries = deliveriesForRight(right.id)
    if (rightDeliveries.length === 0) return 0
    return Math.round((rightDeliveries.filter(isDeliveryComplete).length / rightDeliveries.length) * 100)
  }

  async function fetchAllocation() {
    setLoading(true)
    setError(null)

    const { data, error } = await supabase
      .from('secure_deal_project_allocations')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error || !data) {
      setError(error?.message || 'Allocation not found.')
      setLoading(false)
      return
    }

    const { data: rightsData } = await supabase
      .from('rights')
      .select('*')
      .eq('deal_id', data.deal_id)
      .eq('project_id', data.project_id)
      .order('created_at', { ascending: false })

    const rightIds = (rightsData || []).map(r => r.id)

    let deliveryData = []

    if (rightIds.length > 0) {
      const { data: deliveriesData } = await supabase
        .from('secure_deliveries')
        .select('*')
        .in('right_id', rightIds)
        .order('created_at', { ascending: true })

      deliveryData = deliveriesData || []
    }

    setAllocation(data)
    setRights(rightsData || [])
    setDeliveries(deliveryData)
    setLoading(false)
  }

  async function addRight() {
    if (!newRight.name.trim()) {
      alert('Please enter a right name')
      return
    }

    const quantity = Math.max(1, Number(newRight.quantity) || 1)
    setSaving(true)

    const { data: rightData, error: rightError } = await supabase
      .from('rights')
      .insert([
        withOrg({
          deal_id: allocation.deal_id,
          project_id: allocation.project_id,
          name: newRight.name.trim(),
          right_type: newRight.right_type,
          quantity,
          notes: newRight.notes || null
        }, orgId)
      ])
      .select()
      .single()

    if (rightError) {
      setSaving(false)
      alert(rightError.message)
      return
    }

    const deliveryRows = Array.from({ length: quantity }, (_, index) =>
      withOrg({
        right_id: rightData.id,
        name: `${newRight.name.trim()} ${index + 1}`,
        status: 'pending',
        channel: null,
        notes: null,
        delivery_date: null
      }, orgId)
    )

    const { data: insertedDeliveries, error: deliveryError } = await supabase
      .from('deliveries')
      .insert(deliveryRows)
      .select('id, right_id, status, delivery_date, delivered_by, url, file_url, notes, created_at, assigned_to, org_id, name, channel, custom_fields')

    setSaving(false)

    if (deliveryError) {
      alert(deliveryError.message)
      return
    }

    setRights(prev => [rightData, ...prev])
    setDeliveries(prev => [...prev, ...(insertedDeliveries || [])])
    setOpenRights(prev => ({ ...prev, [rightData.id]: true }))
    setNewRight(EMPTY_RIGHT)
  }

  async function toggleDelivery(delivery) {
    const isDelivered = isDeliveryComplete(delivery)

    const updates = {
      status: isDelivered ? 'pending' : 'delivered',
      delivery_date: isDelivered ? null : new Date().toISOString().slice(0, 10)
    }

    setDeliveries(prev => prev.map(d => d.id === delivery.id ? { ...d, ...updates } : d))

    const { error } = await supabase
      .from('deliveries')
      .update(updates)
      .eq('id', delivery.id)

    if (error) {
      alert(error.message)
      setDeliveries(prev => prev.map(d => d.id === delivery.id ? delivery : d))
    }
  }

  async function updateDelivery(deliveryId, updates) {
    const previousDelivery = deliveries.find(d => d.id === deliveryId)

    setDeliveries(prev => prev.map(d => d.id === deliveryId ? { ...d, ...updates } : d))

    const { error } = await supabase
      .from('deliveries')
      .update(updates)
      .eq('id', deliveryId)

    if (error) {
      alert(error.message)
      if (previousDelivery) {
        setDeliveries(prev => prev.map(d => d.id === deliveryId ? previousDelivery : d))
      }
    }
  }

  async function bulkUpdateDeliveries(rightId, updates) {
    const rightDeliveries = deliveriesForRight(rightId)
    const ids = rightDeliveries.map(d => d.id)

    if (ids.length === 0) return

    const previousDeliveries = deliveries

    setDeliveries(prev =>
      prev.map(d => ids.includes(d.id) ? { ...d, ...updates } : d)
    )

    const { error } = await supabase
      .from('deliveries')
      .update(updates)
      .in('id', ids)

    if (error) {
      alert(error.message)
      setDeliveries(previousDeliveries)
    }
  }

  function setBulkEdit(rightId, updates) {
    setBulkEdits(prev => ({
      ...prev,
      [rightId]: {
        ...(prev[rightId] || {}),
        ...updates
      }
    }))
  }

  async function applyBulkChannel(rightId) {
    const channel = bulkEdits[rightId]?.channel || ''
    await bulkUpdateDeliveries(rightId, { channel: channel || null })
  }

  async function applyBulkDueDate(rightId) {
    const deliveryDate = bulkEdits[rightId]?.delivery_date || null
    await bulkUpdateDeliveries(rightId, { delivery_date: deliveryDate })
  }

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading...
      </div>
    )
  }

  if (error || !allocation) {
    return (
      <div className="hv-page">
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/contracts')} style={{ marginBottom: '20px' }}>
          ← Back to Projects
        </button>

        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px'
        }}>
          {error || 'Allocation not found.'}
        </div>
      </div>
    )
  }

  const sym = currencySymbol(allocation.currency || allocation.deals?.currency)
  const amount = Number(allocation.amount || 0)
  const dealTotal = Number(allocation.deals?.value || 0)
  const completedRights = rights.filter(isRightComplete).length

  return (
    <div className="hv-page">
      <div style={{ marginBottom: '20px' }}>
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate(`/projects/${allocation.project_id}`)}>
          ← Back to Project
        </button>
      </div>

      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">{allocation.deals?.name || 'Allocation'}</h1>
          <p className="hv-page-subtitle">
            {allocation.deals?.clients?.name || 'No client'} · {allocation.projects?.name || 'No project'}
          </p>
        </div>

        <button className="hv-btn hv-btn-secondary" onClick={() => navigate(`/deals/${allocation.deal_id}`)}>
          View Full Deal
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '22px' }}>
        <div className="hv-card" style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Project Allocation</div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{sym}{amount.toLocaleString()}</div>
        </div>

        <div className="hv-card" style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Full Deal Value</div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>
            {currencySymbol(allocation.deals?.currency)}{dealTotal.toLocaleString()}
          </div>
        </div>

        <div className="hv-card" style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Rights</div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{rights.length}</div>
        </div>

        <div className="hv-card" style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Completed Rights</div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{completedRights}/{rights.length}</div>
        </div>
      </div>

      <div className="hv-card" style={{ padding: '24px', marginBottom: '22px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '18px' }}>Add Right</div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '14px', marginBottom: '16px' }}>
          <div>
            <label className="hv-form-label">Right Name</label>
            <input className="hv-input" placeholder="e.g. Social Media Posts" value={newRight.name} onChange={e => setNewRight({ ...newRight, name: e.target.value })} />
          </div>

          <div>
            <label className="hv-form-label">Type</label>
            <select className="hv-select" value={newRight.right_type} onChange={e => setNewRight({ ...newRight, right_type: e.target.value })}>
              <option>Social Media</option>
              <option>Branding</option>
              <option>Hospitality</option>
              <option>Content</option>
              <option>Digital</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="hv-form-label">Quantity</label>
            <input className="hv-input" type="number" min="1" value={newRight.quantity} onChange={e => setNewRight({ ...newRight, quantity: e.target.value })} />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label className="hv-form-label">Notes</label>
            <input className="hv-input" placeholder="Optional notes..." value={newRight.notes} onChange={e => setNewRight({ ...newRight, notes: e.target.value })} />
          </div>
        </div>

        <button className="hv-btn hv-btn-primary" onClick={addRight} disabled={saving}>
          {saving ? 'Saving...' : 'Add Right'}
        </button>
      </div>

      {rights.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">No rights tracked yet</div>
            <div className="hv-empty-text">Add a right above to create delivery/checkoff items.</div>
          </div>
        </div>
      ) : (
        rights.map(right => {
          const rightDeliveries = deliveriesForRight(right.id)
          const rightComplete = rightDeliveries.filter(isDeliveryComplete).length
          const isOpen = openRights[right.id] || false
          const progress = rightProgress(right)

          return (
            <div key={right.id} className="hv-table-wrap" style={{ marginBottom: '22px' }}>
              <div
                onClick={() => toggleRightOpen(right.id)}
                style={{
                  padding: '14px 22px',
                  borderBottom: isOpen ? '1px solid var(--hv-border)' : 'none',
                  background: '#FAFBFD',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{right.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '3px' }}>
                    {right.right_type || 'Right'} · {rightComplete}/{rightDeliveries.length} items completed
                  </div>

                  <div style={{
                    height: '7px',
                    background: 'var(--hv-border)',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    marginTop: '10px',
                    maxWidth: '460px'
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      background: 'var(--hv-accent)',
                      borderRadius: '999px',
                      transition: 'width 0.2s ease'
                    }} />
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: 'var(--hv-accent)', fontWeight: '500' }}>
                  {progress}% · {isOpen ? 'Collapse' : 'Expand'}
                </div>
              </div>

              {isOpen && (
                <>
                  <div style={{
                    padding: '16px 22px',
                    borderBottom: '1px solid var(--hv-border)',
                    background: '#fff'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: '16px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          className="hv-btn hv-btn-secondary"
                          onClick={() => bulkUpdateDeliveries(right.id, {
                            status: 'delivered',
                            delivery_date: new Date().toISOString().slice(0, 10)
                          })}
                        >
                          Mark all complete
                        </button>

                        <button
                          className="hv-btn hv-btn-secondary"
                          onClick={() => bulkUpdateDeliveries(right.id, {
                            status: 'pending',
                            delivery_date: null
                          })}
                        >
                          Mark all pending
                        </button>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap'
                      }}>
                        <div>
                          <label className="hv-form-label">Bulk Channel</label>
                          <select
                            className="hv-select"
                            value={bulkEdits[right.id]?.channel || ''}
                            onChange={e => setBulkEdit(right.id, { channel: e.target.value })}
                            style={{ minWidth: '150px' }}
                          >
                            <option value="">—</option>
                            <option>Twitter</option>
                            <option>Instagram</option>
                            <option>LinkedIn</option>
                            <option>Facebook</option>
                            <option>TikTok</option>
                            <option>YouTube</option>
                            <option>Website</option>
                            <option>Other</option>
                          </select>
                        </div>

                        <button className="hv-btn hv-btn-secondary" onClick={() => applyBulkChannel(right.id)}>
                          Apply
                        </button>

                        <div>
                          <label className="hv-form-label">Bulk Due Date</label>
                          <input
                            className="hv-input"
                            type="date"
                            value={bulkEdits[right.id]?.delivery_date || ''}
                            onChange={e => setBulkEdit(right.id, { delivery_date: e.target.value })}
                            style={{ minWidth: '160px' }}
                          />
                        </div>

                        <button className="hv-btn hv-btn-secondary" onClick={() => applyBulkDueDate(right.id)}>
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>

                  <table className="hv-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Channel</th>
                        <th>Due Date</th>
                        <th>Proof URL</th>
                        <th>Notes</th>
                        <th style={{ textAlign: 'right' }}>Done</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rightDeliveries.map(delivery => (
                        <tr key={delivery.id}>
                          <td>
                            <input className="hv-input" defaultValue={delivery.name || ''} placeholder="Delivery item name..." onBlur={e => updateDelivery(delivery.id, { name: e.target.value || null })} />
                          </td>

                          <td>
                            <select className="hv-select" value={delivery.channel || ''} onChange={e => updateDelivery(delivery.id, { channel: e.target.value || null })}>
                              <option value="">—</option>
                              <option>Twitter</option>
                              <option>Instagram</option>
                              <option>LinkedIn</option>
                              <option>Facebook</option>
                              <option>TikTok</option>
                              <option>YouTube</option>
                              <option>Website</option>
                              <option>Other</option>
                            </select>
                          </td>

                          <td>
                            <input className="hv-input" type="date" value={delivery.delivery_date || ''} onChange={e => updateDelivery(delivery.id, { delivery_date: e.target.value || null })} />
                          </td>

                          <td>
                            <input className="hv-input" placeholder="https://..." defaultValue={delivery.url || ''} onBlur={e => updateDelivery(delivery.id, { url: e.target.value || null })} />
                          </td>

                          <td>
                            <input className="hv-input" placeholder="Notes..." defaultValue={delivery.notes || ''} onBlur={e => updateDelivery(delivery.id, { notes: e.target.value || null })} />
                          </td>

                          <td style={{ textAlign: 'right' }}>
                            <input type="checkbox" checked={isDeliveryComplete(delivery)} onChange={() => toggleDelivery(delivery)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
